/**
 * Mobile orientation: keep the main UI portrait; allow real landscape only
 * while music-video cinema is active (playing / forced fullscreen).
 *
 * Soft-lock is CSS-driven via html datasets (no transform flash from late JS).
 * Screen Orientation API is best-effort (Android Chrome / some PWAs).
 */

type OrientationLockType =
  | 'any'
  | 'natural'
  | 'landscape'
  | 'portrait'
  | 'portrait-primary'
  | 'portrait-secondary'
  | 'landscape-primary'
  | 'landscape-secondary';

type ScreenOrientationWithLock = ScreenOrientation & {
  lock?: (orientation: OrientationLockType) => Promise<void>;
};

let cinemaActive = false;
let videoPlaying = false;
let apiLockPreferred: 'portrait' | 'landscape' | 'none' = 'portrait';

function root(): HTMLElement {
  return document.documentElement;
}

function applyDatasets() {
  const el = root();
  if (videoPlaying) el.dataset.videoPlaying = '1';
  else delete el.dataset.videoPlaying;

  // Only lift the portrait soft-lock once cinema is actually active.
  // Allowing landscape while merely "playing" flashes the landscape site shell
  // for a frame before the video cinema / native FS paints.
  if (cinemaActive) el.dataset.allowLandscape = '1';
  else delete el.dataset.allowLandscape;
}

async function lockOrientation(type: OrientationLockType) {
  try {
    const orientation = screen.orientation as ScreenOrientationWithLock;
    await orientation.lock?.(type);
  } catch {
    /* iOS / unsigned PWAs reject */
  }
}

async function unlockOrientation() {
  try {
    screen.orientation?.unlock?.();
  } catch {
    /* ignore */
  }
}

async function syncApiLock() {
  if (apiLockPreferred === 'none') {
    await unlockOrientation();
    return;
  }
  if (apiLockPreferred === 'landscape') {
    await unlockOrientation();
    await lockOrientation('landscape');
    return;
  }
  await unlockOrientation();
  await lockOrientation('portrait');
}

/** Call from now-playing when video playback / cinema state changes. */
export function setMobileVideoOrientation(opts: {
  playing: boolean;
  cinema: boolean;
  /** True only for physical landscape cinema — never for forced portrait expand. */
  lockLandscape?: boolean;
}) {
  videoPlaying = opts.playing;
  cinemaActive = opts.cinema;
  applyDatasets();

  if (opts.cinema && opts.lockLandscape) {
    apiLockPreferred = 'landscape';
  } else if (opts.cinema || opts.playing) {
    // Portrait cinema / sheet playback — unlock so a physical rotate can enter.
    apiLockPreferred = 'none';
  } else {
    apiLockPreferred = 'portrait';
  }
  void syncApiLock();
}

export async function lockLandscapeOrientation() {
  apiLockPreferred = 'landscape';
  cinemaActive = true;
  applyDatasets();
  await unlockOrientation();
  await lockOrientation('landscape');
}

/** Leave cinema — reclaim portrait (API when available; CSS soft-lock otherwise). */
export async function restorePortraitOrientation() {
  videoPlaying = false;
  cinemaActive = false;
  apiLockPreferred = 'portrait';
  delete root().dataset.allowLandscape;
  delete root().dataset.videoPlaying;
  await unlockOrientation();
  await lockOrientation('portrait');
}

/** Boot: portrait preference until video cinema needs landscape. */
export function initMobileOrientation() {
  applyDatasets();
  apiLockPreferred = 'portrait';
  void syncApiLock();

  // Re-assert portrait after return from another app (some Androids drop the lock).
  const onVisible = () => {
    if (document.visibilityState !== 'visible') return;
    if (apiLockPreferred === 'portrait') void syncApiLock();
  };
  document.addEventListener('visibilitychange', onVisible);
}
